/**
 * Small-supplies surcharge on one work order (#2644).
 *
 * The work order carries its own copy of the surcharge, taken from the
 * workshop default when it was created. This row shows that copy and lets
 * the office change or remove it for this job only, until the work order is
 * billed. The amount shown is what the totals already include.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { useUpdateBooking } from '@/hooks/useBookings';
import { useNotify } from '@/hooks/useNotify';
import type { WorkOrderDetail } from '@/hooks/useWorkOrders';
import { describeApiError } from '@/lib/describeApiError';
import { eurosToCents, normalizeEuroInput } from '@/utils/money';
import type { SmallSuppliesSurcharge, SurchargeType } from '@/types';

interface WorkOrderSurchargeRowProps {
  workOrder: WorkOrderDetail;
  /** Billing is locked (invoiced, closed status or no permission): read-only. */
  locked: boolean;
}

/** Module scope: basis → label key in the `settings` namespace (shared with the workshop form). */
const BASIS_LABEL_KEY: Record<SurchargeType, string> = {
  labor: 'smallSuppliesSurcharge.basisLabor',
  parts: 'smallSuppliesSurcharge.basisParts',
  total: 'smallSuppliesSurcharge.basisTotal',
  fixed: 'smallSuppliesSurcharge.basisFixed',
};

const BASIS_SUMMARY_KEY: Record<SurchargeType, string> = {
  labor: 'detail.surchargeOfLabor',
  parts: 'detail.surchargeOfParts',
  total: 'detail.surchargeOfTotal',
  fixed: 'detail.surchargeFixed',
};

function formatPercent(value: number): string {
  return String(value).replace('.', ',');
}

function formatEuros(cents: number): string {
  return (cents / 100).toFixed(2).replace('.', ',');
}

export function describeSurcharge(
  t: (key: string, options?: Record<string, unknown>) => string,
  config: SmallSuppliesSurcharge,
): string {
  if (config.type === 'fixed') {
    return t(BASIS_SUMMARY_KEY.fixed, { amount: formatEuros(config.fixedCents ?? 0) });
  }
  return t(BASIS_SUMMARY_KEY[config.type], { percent: formatPercent(config.percentage ?? 0) });
}

export function WorkOrderSurchargeRow({ workOrder, locked }: WorkOrderSurchargeRowProps) {
  const { t } = useTranslation('workorders');
  const { t: ts } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const updateBooking = useUpdateBooking();

  const current = workOrder.smallSuppliesSurcharge ?? null;
  const [open, setOpen] = useState(false);
  const [enabled, setEnabled] = useState(current !== null);
  const [basis, setBasis] = useState<SurchargeType>(current?.type ?? 'labor');
  const [amount, setAmount] = useState('');

  useEffect(() => {
    if (!open) return;
    setEnabled(current !== null);
    setBasis(current?.type ?? 'labor');
    setAmount(
      current === null
        ? ''
        : current.type === 'fixed'
          ? formatEuros(current.fixedCents ?? 0)
          : formatPercent(current.percentage ?? 0),
    );
  }, [open, current]);

  const parsedAmount = (() => {
    if (!enabled) return null;
    const normalized = normalizeEuroInput(amount);
    if (normalized === '') return null;
    if (basis === 'fixed') return eurosToCents(normalized);
    const percent = Number(normalized);
    return Number.isFinite(percent) && percent >= 0 && percent <= 100 ? percent : null;
  })();
  const canSave = !updateBooking.isPending && (!enabled || parsedAmount !== null);

  const handleSave = async () => {
    if (!canSave) return;
    const next: SmallSuppliesSurcharge | null = !enabled
      ? null
      : basis === 'fixed'
        ? { type: 'fixed', fixedCents: parsedAmount ?? 0 }
        : { type: basis, percentage: parsedAmount ?? 0 };
    try {
      await updateBooking.mutateAsync({ id: workOrder.id, data: { smallSuppliesSurcharge: next } });
      notify.success(t('detail.surchargeSaved'));
      setOpen(false);
    } catch (error) {
      notify.error(t('detail.surchargeSaveFailed'), describeApiError(error, tc, { fallback: tc('unknownError') }));
    }
  };

  // The amount itself is the "Pientarvikelisä" billing line above, in gross
  // like every other line (docs/VAT-BASIS.md). Showing the net figure here as
  // well read as two different surcharges (#2923); this row only carries the
  // rule and the edit control.
  const summary = current ? describeSurcharge(t, current) : t('detail.noSurcharge');

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 0.5 }} data-testid="billing-surcharge-row">
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" color="text.secondary">
            {t('detail.smallSuppliesSurcharge')}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {summary}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {!locked && (
            <Tooltip title={current ? t('detail.editSurcharge') : t('detail.addSurcharge')}>
              <IconButton
                size="small"
                aria-label={current ? t('detail.editSurcharge') : t('detail.addSurcharge')}
                onClick={() => setOpen(true)}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      <ResponsiveDialog
        open={open}
        onClose={updateBooking.isPending ? undefined : () => setOpen(false)}
        mobileTitle={t('detail.surchargeDialogTitle')}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('detail.surchargeDialogTitle')}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <FormControlLabel
            control={<Switch checked={enabled} onChange={(_event, checked) => setEnabled(checked)} />}
            label={t('detail.surchargeEnabled')}
          />
          {enabled && (
            <>
              <FormControl size="small" fullWidth>
                <InputLabel id="wo-surcharge-basis">{ts('smallSuppliesSurcharge.basis')}</InputLabel>
                <Select
                  labelId="wo-surcharge-basis"
                  label={ts('smallSuppliesSurcharge.basis')}
                  value={basis}
                  onChange={(event) => { setBasis(event.target.value as SurchargeType); setAmount(''); }}
                >
                  {(Object.keys(BASIS_LABEL_KEY) as SurchargeType[]).map((key) => (
                    <MenuItem key={key} value={key}>{ts(BASIS_LABEL_KEY[key])}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small"
                fullWidth
                label={basis === 'fixed' ? ts('smallSuppliesSurcharge.fixedAmount') : ts('smallSuppliesSurcharge.percent')}
                helperText={basis === 'fixed' ? ts('smallSuppliesSurcharge.fixedAmountHint') : ts('smallSuppliesSurcharge.percentHint')}
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
                slotProps={{ htmlInput: { inputMode: 'decimal' } }}
                error={enabled && amount !== '' && parsedAmount === null}
              />
            </>
          )}
          <Typography variant="caption" color="text.secondary">
            {t('detail.surchargeHint')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={updateBooking.isPending}>{tc('cancel')}</Button>
          <Button variant="contained" onClick={() => { void handleSave(); }} disabled={!canSave}>
            {tc('save')}
          </Button>
        </DialogActions>
      </ResponsiveDialog>
    </>
  );
}
